import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, useLocation, useParams, useNavigate } from 'react-router-dom';
import { MainLayout } from '../components/MainLayout';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import {
  getTeam,
  inviteTeamMember,
  leaveTeam,
  removeTeamMember,
  updateTeam,
  updateTeamMember,
  updateTeamPermissions,
  getTeamVault,
  getTeamCategories,
  getTeamActivity,
  cancelTeamInvitation,
  getTeamInvitations,
} from '../api/teams';
import { getShareGrant } from '../api/sharing';
import { decryptShareGrant } from '../utils/shareCrypto';
import { useAuth } from '../contexts/AuthContext';
import { Shield, Settings, Users, Folder, LockKeyhole, Mail, Activity, Eye, EyeOff, Trash2, ArrowLeft, Plus, X, Copy, Key, Globe, FileText, CreditCard, User, Check } from 'lucide-react';

const ITEM_TYPE_ICONS = {
  PASSWORD: Key,
  LOGIN: Key,
  SECURE_NOTE: FileText,
  IDENTITY: User,
  PAYMENT_CARD: CreditCard,
};

const ITEM_TYPE_LABELS = {
  PASSWORD: 'Password',
  LOGIN: 'Password',
  SECURE_NOTE: 'Secure Note',
  IDENTITY: 'Identity',
  PAYMENT_CARD: 'Payment Card',
};

function parseJsonSafe(value) {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try { return JSON.parse(value); } catch { return null; }
}

// ── SharedVaultItem ──────────────────────────────────────────────────────────
function SharedVaultItem({ item, membership }) {
  const { vaultKey } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [decrypted, setDecrypted] = useState(null);
  const [decryptError, setDecryptError] = useState(false);
  const [decrypting, setDecrypting] = useState(false);
  const [copying, setCopying] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState('');

  const permissions = useMemo(() => {
    const raw = parseJsonSafe(item.permission);
    if (Array.isArray(raw)) return raw.map(p => String(p).toUpperCase());
    if (typeof raw === 'string') return [raw.toUpperCase()];
    return ['VIEW'];
  }, [item.permission]);

  const canView = permissions.includes('VIEW');
  const canCopy = permissions.includes('COPY');

  // Auto-decrypt in background when active so metadata displays nicely
  useEffect(() => {
    let active = true;
    if (item.status === 'ACTIVE' && !decrypted) {
      const autoDecrypt = async () => {
        try {
          let grant = item;
          if (!grant.encryptedData && !grant.encrypted_data) {
            grant = await getShareGrant(item.id);
          }
          const data = await decryptShareGrant(grant, vaultKey);
          if (active && data) {
            setDecrypted(data);
          }
        } catch {
          // Will be handled if user clicks View/Copy
        }
      };
      autoDecrypt();
    }
    return () => { active = false; };
  }, [item.id, item.status, vaultKey]);

  const fetchAndDecrypt = useCallback(async () => {
    if (decrypted) return decrypted;
    let grant = item;
    if (!grant.encryptedData && !grant.encrypted_data) {
      grant = await getShareGrant(item.id);
    }
    const data = await decryptShareGrant(grant, vaultKey);
    setDecrypted(data);
    return data;
  }, [decrypted, item, vaultKey]);

  const handleCopy = useCallback(async (field, value) => {
    try {
      await navigator.clipboard.writeText(value || '');
      setCopied(field);
      setTimeout(() => setCopied(''), 2000);
    } catch { /* ignore */ }
  }, []);

  const handleCopyPassword = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCopying(true);
    try {
      let pwd = decrypted?.password;
      if (!pwd) {
        const data = await fetchAndDecrypt();
        pwd = data?.password;
      }
      if (!pwd) {
        throw new Error('No password found in this item');
      }
      await navigator.clipboard.writeText(pwd);
      setCopied('password');
      setTimeout(() => setCopied(''), 2000);
    } catch (err) {
      console.error('Failed to copy password:', err);
      alert('Unable to copy password: ' + (err.message || 'Decryption failed'));
    } finally {
      setCopying(false);
    }
  };

  const handleToggleView = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (expanded) {
      setExpanded(false);
      return;
    }
    if (decrypted) {
      setExpanded(true);
      return;
    }

    setDecrypting(true);
    setDecryptError(false);
    try {
      await fetchAndDecrypt();
      setExpanded(true);
    } catch (err) {
      console.error('Failed to view shared item:', err);
      setDecryptError(true);
      setExpanded(true);
    } finally {
      setDecrypting(false);
    }
  };

  const expiresAt = item.expiresAt ? new Date(item.expiresAt) : null;
  const isNearExpiry = expiresAt && (expiresAt - Date.now()) < 30 * 60 * 1000; // < 30 min
  const TypeIcon = ITEM_TYPE_ICONS[item.itemType] || LockKeyhole;
  const isPasswordType = item.itemType === 'PASSWORD' || item.itemType === 'LOGIN';

  return (
    <div style={{
      background: 'var(--bg-secondary)',
      borderRadius: '10px',
      border: '1px solid var(--border-color)',
      overflow: 'hidden',
      transition: 'box-shadow 0.2s',
    }}>
      {/* Row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px' }}>
        <div style={{
          width: '36px', height: '36px', borderRadius: '8px',
          background: 'var(--accent-primary)', opacity: 0.12,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
        }} />
        <TypeIcon size={18} color="var(--accent-primary)" style={{ position: 'absolute', marginLeft: '9px' }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: '600', fontSize: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            {decrypted?.title || decrypted?.name || ITEM_TYPE_LABELS[item.itemType] || 'Vault Item'}
            <span style={{
              fontSize: '11px', padding: '2px 7px', borderRadius: '999px',
              background: 'rgba(99,102,241,0.12)', color: 'var(--accent-primary)', fontWeight: '500'
            }}>
              {ITEM_TYPE_LABELS[item.itemType] || item.itemType}
            </span>
            {isNearExpiry && (
              <span style={{
                fontSize: '11px', padding: '2px 7px', borderRadius: '999px',
                background: 'rgba(245,158,11,0.12)', color: '#f59e0b', fontWeight: '500'
              }}>Expiring soon</span>
            )}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '2px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {decrypted?.username && <span>@{decrypted.username}</span>}
            {decrypted?.url && <span>🌐 {decrypted.url}</span>}
            <span>Permissions: {permissions.join(', ')}</span>
            {expiresAt && <span>Expires: {expiresAt.toLocaleString()}</span>}
            {item.accessCount != null && <span>Accessed: {item.accessCount}{item.maxAccessCount ? ` / ${item.maxAccessCount}` : ''}</span>}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
          {canCopy && (
            <Button
              type="button"
              variant="primary"
              disabled={copying || item.status !== 'ACTIVE'}
              onClick={handleCopyPassword}
              style={{ padding: '6px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              {copied === 'password' ? <Check size={14} color="#22c55e" /> : <Copy size={14} />}
              {copied === 'password' ? 'Copied!' : copying ? 'Copying...' : 'Copy Password'}
            </Button>
          )}
          {canView && (
            <Button
              type="button"
              variant="secondary"
              disabled={decrypting || item.status !== 'ACTIVE'}
              onClick={handleToggleView}
              style={{ padding: '6px 14px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              {decrypting ? '...' : expanded ? <><EyeOff size={14} /> Hide</> : <><Eye size={14} /> View</>}
            </Button>
          )}
        </div>
      </div>

      {/* Expanded details */}
      {expanded && canView && (
        <div style={{
          borderTop: '1px solid var(--border-color)',
          padding: '16px 20px',
          background: 'var(--bg-primary)',
          display: 'flex', flexDirection: 'column', gap: '12px'
        }}>
          {decryptError ? (
            <div style={{ color: 'var(--danger)', fontSize: '13px' }}>
              ⚠ Could not decrypt this item. You may not have the correct key, or the share grant may have expired.
            </div>
          ) : decrypted ? (
            <>
              {/* PASSWORD / LOGIN fields */}
              {isPasswordType && (
                <>
                  {(decrypted.title || decrypted.name) && <Field label="Name" value={decrypted.title || decrypted.name} />}
                  {decrypted.username && (
                    <Field label="Username" value={decrypted.username}
                      onCopy={canCopy ? () => handleCopy('username', decrypted.username) : null}
                      copied={copied === 'username'} />
                  )}
                  {decrypted.password && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <label style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Password</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontFamily: 'monospace', fontSize: '14px', flex: 1,
                          filter: showPassword ? 'none' : 'blur(5px)', userSelect: showPassword ? 'auto' : 'none',
                          transition: 'filter 0.2s', background: 'var(--bg-secondary)',
                          padding: '6px 10px', borderRadius: '6px' }}>
                          {decrypted.password}
                        </span>
                        <button type="button" onClick={() => setShowPassword(p => !p)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px' }}>
                          {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                        {canCopy && (
                          <button type="button" onClick={() => handleCopy('password', decrypted.password)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px' }}>
                            <Copy size={16} />
                          </button>
                        )}
                      </div>
                      {copied === 'password' && <span style={{ fontSize: '12px', color: '#22c55e' }}>Copied to clipboard!</span>}
                    </div>
                  )}
                  {decrypted.url && <Field label="Website" value={decrypted.url} isLink />}
                  {decrypted.notes && <Field label="Notes" value={decrypted.notes} multiline />}
                </>
              )}
              {/* SECURE_NOTE fields */}
              {item.itemType === 'SECURE_NOTE' && (
                <>
                  {(decrypted.title || decrypted.name) && <Field label="Title" value={decrypted.title || decrypted.name} />}
                  {decrypted.content && <Field label="Content" value={decrypted.content} multiline />}
                  {decrypted.notes && <Field label="Notes" value={decrypted.notes} multiline />}
                </>
              )}
              {/* IDENTITY fields */}
              {item.itemType === 'IDENTITY' && (
                <>
                  {(decrypted.title || decrypted.name) && <Field label="Name" value={decrypted.title || decrypted.name} />}
                  {decrypted.firstName && <Field label="First Name" value={`${decrypted.firstName} ${decrypted.lastName || ''}`.trim()} />}
                  {decrypted.email && <Field label="Email" value={decrypted.email} onCopy={canCopy ? () => handleCopy('email', decrypted.email) : null} copied={copied === 'email'} />}
                  {decrypted.phone && <Field label="Phone" value={decrypted.phone} />}
                  {decrypted.company && <Field label="Company" value={decrypted.company} />}
                </>
              )}
              {/* PAYMENT_CARD fields */}
              {item.itemType === 'PAYMENT_CARD' && (
                <>
                  {(decrypted.title || decrypted.name) && <Field label="Card Label" value={decrypted.title || decrypted.name} />}
                  {decrypted.cardholderName && <Field label="Cardholder" value={decrypted.cardholderName} />}
                  {decrypted.cardNumber && (
                    <Field label="Card Number"
                      value={showPassword ? decrypted.cardNumber : '**** **** **** ' + decrypted.cardNumber.slice(-4)}
                      onCopy={canCopy ? () => handleCopy('cardNumber', decrypted.cardNumber) : null}
                      copied={copied === 'cardNumber'}
                      onToggleShow={() => setShowPassword(p => !p)}
                      masked={!showPassword} />
                  )}
                  {decrypted.expiryMonth && (
                    <Field label="Expires" value={`${decrypted.expiryMonth} / ${decrypted.expiryYear || ''}`} />
                  )}
                </>
              )}
              {/* Custom fields */}
              {decrypted.customFields?.map((cf, i) => (
                <Field key={i} label={cf.label} value={cf.value}
                  masked={cf.type === 'hidden' && !showPassword}
                  onCopy={canCopy ? () => handleCopy(`cf_${i}`, cf.value) : null}
                  copied={copied === `cf_${i}`} />
              ))}
            </>
          ) : (
            <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading details...</div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, value, isLink, multiline, masked, onCopy, copied: isCopied, onToggleShow }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <label style={{ fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
        {isLink ? (
          <a href={value} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: '14px', color: 'var(--accent-primary)', flex: 1, wordBreak: 'break-all' }}>{value}</a>
        ) : (
          <span style={{
            fontSize: '14px', flex: 1, wordBreak: 'break-all',
            fontFamily: masked ? 'monospace' : 'inherit',
            filter: masked ? 'blur(5px)' : 'none',
            userSelect: masked ? 'none' : 'auto',
            background: 'var(--bg-secondary)', padding: '6px 10px', borderRadius: '6px',
            whiteSpace: multiline ? 'pre-wrap' : 'normal',
          }}>{value}</span>
        )}
        {onToggleShow && (
          <button onClick={onToggleShow} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '4px', marginTop: '4px' }}>
            {masked ? <Eye size={15} /> : <EyeOff size={15} />}
          </button>
        )}
        {onCopy && (
          <button onClick={onCopy} style={{ background: 'none', border: 'none', cursor: 'pointer', color: isCopied ? '#22c55e' : 'var(--text-secondary)', padding: '4px', marginTop: '4px' }}>
            <Copy size={15} />
          </button>
        )}
      </div>
      {isCopied && <span style={{ fontSize: '11px', color: '#22c55e' }}>Copied!</span>}
    </div>
  );
}

// ── TeamDetail (main) ────────────────────────────────────────────────────────

const tabs = [
  { label: 'Overview', path: '', icon: Shield },
  { label: 'Members', path: 'members', icon: Users },
  { label: 'Shared Vault', path: 'shared', icon: LockKeyhole },
  { label: 'Categories', path: 'categories', icon: Folder },
  { label: 'Invitations', path: 'invitations', icon: Mail },
  { label: 'Permissions', path: 'permissions', icon: Shield },
  { label: 'Activity', path: 'activity', icon: Activity },
  { label: 'Settings', path: 'settings', icon: Settings },
];

export default function TeamDetail() {
  const { teamId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const activeTab = location.pathname.split('/').filter(Boolean).slice(2)[0] || '';
  
  const [team, setTeam] = useState(null);
  const [vaultItems, setVaultItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [teamInvitations, setTeamInvitations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Invite Drawer
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState('MEMBER');
  const [expiresIn, setExpiresIn] = useState('7 days');
  
  // Settings Form
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  
  const [permissionsJson, setPermissionsJson] = useState('');

  const fetchTeamData = async () => {
    setLoading(true);
    try {
      const data = await getTeam(teamId);
      setTeam(data);
      setTeamName(data.name || '');
      setTeamDescription(data.description || '');
      setPermissionsJson(JSON.stringify(data.permissions || {}, null, 2));
      
      const [vaultData, catData, invData] = await Promise.allSettled([
        getTeamVault(teamId),
        getTeamCategories(teamId),
        getTeamInvitations(teamId),
      ]);
      setVaultItems(vaultData.status === 'fulfilled' ? (vaultData.value || []) : []);
      setCategories(catData.status === 'fulfilled' ? (catData.value || []) : []);
      setTeamInvitations(invData.status === 'fulfilled' ? (invData.value || []) : []);
    } catch (error) {
      console.error('Failed to load team data', error);
      if (error?.response?.status === 403 || error?.response?.status === 404) {
        navigate('/teams');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamData();
  }, [teamId]);

  const handleInvite = async (event) => {
    event.preventDefault();
    if (!memberEmail.trim()) return;

    try {
      await inviteTeamMember(teamId, { email: memberEmail, role: memberRole, expiresIn });
      setMemberEmail('');
      setMemberRole('MEMBER');
      setIsInviteOpen(false);
      await fetchTeamData();
    } catch (error) {
      console.error('Failed to invite member', error);
    }
  };

  const handleUpdateTeam = async (event) => {
    event.preventDefault();
    try {
      await updateTeam(teamId, { name: teamName, description: teamDescription });
      await fetchTeamData();
    } catch (e) { console.error('Update failed', e); }
  };

  const handleSavePermissions = async () => {
    try {
      const parsed = JSON.parse(permissionsJson);
      await updateTeamPermissions(teamId, parsed);
      await fetchTeamData();
    } catch (e) { console.error('Invalid JSON', e); }
  };

  const handleChangeRole = async (memberId, nextRole) => {
    await updateTeamMember(teamId, memberId, { role: nextRole });
    await fetchTeamData();
  };

  const handleRemoveMember = async (memberId) => {
    if (confirm('Are you sure you want to remove this member?')) {
      await removeTeamMember(teamId, memberId);
      await fetchTeamData();
    }
  };

  const handleLeave = async () => {
    if (confirm('Are you sure you want to leave this team?')) {
      try {
        await leaveTeam(teamId);
        navigate('/teams');
      } catch (e) {
        alert(e.response?.data?.message || 'Failed to leave team');
      }
    }
  };

  if (loading && !team) {
    return <MainLayout><div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading team...</div></MainLayout>;
  }

  return (
    <MainLayout>
      {/* Top Header Banner */}
      <div className="notes-hero-banner">
        <div className="notes-hero-content">
          <div>
            <Link to="/teams" className="notes-hero-btn-secondary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 12px', borderRadius: '8px', textDecoration: 'none', fontSize: '13px', marginBottom: '10px' }}>
              <ArrowLeft size={15} /> Back to teams
            </Link>
            <h1 className="notes-hero-title" style={{ margin: '0 0 6px', display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Shield size={26} color="#ffffff" />
              {team?.name}
              <span className="notes-hero-badge" style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px' }}>
                {team?.status}
              </span>
            </h1>
            <p className="notes-hero-subtitle" style={{ margin: 0 }}>{team?.description || 'No description provided'}</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button variant="ghost" className="notes-hero-btn-secondary" onClick={fetchTeamData}>Refresh</Button>
            <Button className="notes-hero-btn-primary" onClick={() => setIsInviteOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Plus size={16} /> Invite Member
            </Button>
          </div>
        </div>
      </div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '32px',marginBottom: '32px', borderBottom: '1px solid var(--border-color)', overflowX: 'auto' }}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.path;
            return (
              <Link
                key={tab.label}
                to={tab.path ? `/teams/${teamId}/${tab.path}` : `/teams/${teamId}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px',
                  color: isActive ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  textDecoration: 'none', fontWeight: '500', fontSize: '14px',
                  borderBottom: isActive ? '2px solid var(--accent-primary)' : '2px solid transparent',
                  whiteSpace: 'nowrap'
                }}
              >
                <Icon size={16} /> {tab.label}
              </Link>
            );
          })}
        </div>

      <div>
        {/* OVERVIEW TAB */}
        {activeTab === '' && (
          <div style={{ display: 'grid', gap: '24px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
              <div className="glass-panel" style={{ padding: '24px', borderRadius: '12px' }}>
                <div style={{ color: 'var(--text-secondary)', fontSize: '13px', textTransform: 'uppercase' }}>Total Members</div>
                <div style={{ fontSize: '32px', fontWeight: '600', marginTop: '8px' }}>{team?.members?.length || 0}</div>
              </div>
              <div className="glass-panel" style={{ padding: '24px', borderRadius: '12px' }}>
                <div style={{ color: 'var(--text-secondary)', fontSize: '13px', textTransform: 'uppercase' }}>Shared Items</div>
                <div style={{ fontSize: '32px', fontWeight: '600', marginTop: '8px' }}>{vaultItems.length}</div>
              </div>
              <div className="glass-panel" style={{ padding: '24px', borderRadius: '12px' }}>
                <div style={{ color: 'var(--text-secondary)', fontSize: '13px', textTransform: 'uppercase' }}>Categories</div>
                <div style={{ fontSize: '32px', fontWeight: '600', marginTop: '8px' }}>{categories.length}</div>
              </div>
            </div>

            <div className="glass-panel" style={{ padding: '24px', borderRadius: '12px' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>Recent Activity</h3>
              {team?.activity?.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {team.activity.slice(0, 5).map(act => (
                    <div key={act.id} style={{ display: 'flex', gap: '16px', padding: '12px 0', borderBottom: '1px solid var(--border-color)' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500' }}>{act.action.replace(/_/g, ' ')}</div>
                        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>by {act.user_email}</div>
                      </div>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                        {new Date(act.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ color: 'var(--text-secondary)' }}>No recent activity.</div>
              )}
            </div>
          </div>
        )}

        {/* MEMBERS TAB */}
        {activeTab === 'members' && (
          <div className="glass-panel" style={{ borderRadius: '12px', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)', textAlign: 'left', borderBottom: '1px solid var(--border-color)' }}>
                  <th style={{ padding: '16px 24px', fontWeight: '500', color: 'var(--text-secondary)' }}>Member</th>
                  <th style={{ padding: '16px 24px', fontWeight: '500', color: 'var(--text-secondary)' }}>Role</th>
                  <th style={{ padding: '16px 24px', fontWeight: '500', color: 'var(--text-secondary)' }}>Status</th>
                  <th style={{ padding: '16px 24px', fontWeight: '500', color: 'var(--text-secondary)' }}>Joined</th>
                  <th style={{ padding: '16px 24px', fontWeight: '500', color: 'var(--text-secondary)', textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {team?.members?.map((member) => (
                  <tr key={member.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '16px 24px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <img src={`https://ui-avatars.com/api/?name=${member.email}&background=random`} alt="" style={{ width: '32px', height: '32px', borderRadius: '50%' }} />
                        <div>
                          <div style={{ fontWeight: '500' }}>{member.email?.split('@')[0]}</div>
                          <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{member.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '16px 24px' }}>
                      <select 
                        value={member.role} 
                        onChange={(e) => handleChangeRole(member.id, e.target.value)}
                        disabled={member.userId === team.ownerId && team.membership?.role !== 'OWNER'}
                        style={{ padding: '6px 12px', borderRadius: '6px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}
                      >
                        <option value="MEMBER">Member</option>
                        <option value="MANAGER">Manager</option>
                        <option value="OWNER">Owner</option>
                      </select>
                    </td>
                    <td style={{ padding: '16px 24px' }}>
                      <span style={{ padding: '4px 8px', background: member.status === 'ACTIVE' ? 'rgba(34, 197, 94, 0.1)' : 'var(--bg-secondary)', color: member.status === 'ACTIVE' ? '#22c55e' : 'var(--text-secondary)', borderRadius: '12px', fontSize: '12px', fontWeight: '500' }}>
                        {member.status}
                      </span>
                    </td>
                    <td style={{ padding: '16px 24px', color: 'var(--text-secondary)', fontSize: '14px' }}>
                      {new Date(member.joinedAt).toLocaleDateString()}
                    </td>
                    <td style={{ padding: '16px 24px', textAlign: 'right' }}>
                      <Button variant="danger" onClick={() => handleRemoveMember(member.id)} style={{ padding: '6px 12px' }}>Remove</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* SHARED VAULT TAB */}
        {activeTab === 'shared' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div className="glass-panel" style={{ padding: '24px', borderRadius: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '18px' }}>Shared Vault Items</h3>
                <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                  {vaultItems.filter(i => i.status === 'ACTIVE').length} active item{vaultItems.filter(i => i.status === 'ACTIVE').length !== 1 ? 's' : ''}
                </span>
              </div>
              {vaultItems.length === 0 ? (
                <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
                  <LockKeyhole size={40} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
                  <p style={{ margin: 0 }}>No vault items have been shared with this team yet.</p>
                  <p style={{ margin: '8px 0 0', fontSize: '13px' }}>Go to your Vault, open an item, and share it with this team.</p>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {vaultItems.map(item => (
                    <SharedVaultItem
                      key={item.id}
                      item={item}
                      membership={team?.membership}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* INVITATIONS TAB */}
        {activeTab === 'invitations' && (
          <div className="glass-panel" style={{ borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>Team Invitations</h3>
              <Button onClick={() => setIsInviteOpen(true)} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Plus size={16} /> Invite Member
              </Button>
            </div>
            {teamInvitations.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
                <Mail size={36} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
                <p style={{ margin: 0 }}>No invitations sent yet.</p>
                <p style={{ margin: '8px 0 0', fontSize: '13px' }}>Invite members to collaborate on this team.</p>
              </div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--bg-secondary)', textAlign: 'left', borderBottom: '1px solid var(--border-color)' }}>
                    <th style={{ padding: '14px 24px', fontWeight: '500', color: 'var(--text-secondary)', fontSize: '13px' }}>Email</th>
                    <th style={{ padding: '14px 24px', fontWeight: '500', color: 'var(--text-secondary)', fontSize: '13px' }}>Role</th>
                    <th style={{ padding: '14px 24px', fontWeight: '500', color: 'var(--text-secondary)', fontSize: '13px' }}>Status</th>
                    <th style={{ padding: '14px 24px', fontWeight: '500', color: 'var(--text-secondary)', fontSize: '13px' }}>Invited By</th>
                    <th style={{ padding: '14px 24px', fontWeight: '500', color: 'var(--text-secondary)', fontSize: '13px' }}>Expires</th>
                    <th style={{ padding: '14px 24px', fontWeight: '500', color: 'var(--text-secondary)', fontSize: '13px', textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {teamInvitations.map((inv) => {
                    const isPending = inv.status === 'PENDING';
                    const isExpired = inv.expiresAt && new Date(inv.expiresAt) < new Date();
                    const displayStatus = isExpired && isPending ? 'EXPIRED' : inv.status;
                    const statusColor = displayStatus === 'PENDING' ? '#f59e0b' : displayStatus === 'ACCEPTED' ? '#22c55e' : '#ef4444';
                    return (
                      <tr key={inv.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                        <td style={{ padding: '14px 24px', fontWeight: '500' }}>{inv.email}</td>
                        <td style={{ padding: '14px 24px' }}>
                          <span style={{ padding: '3px 8px', background: 'var(--bg-secondary)', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>
                            {inv.role}
                          </span>
                        </td>
                        <td style={{ padding: '14px 24px' }}>
                          <span style={{ padding: '3px 8px', borderRadius: '999px', fontSize: '12px', fontWeight: '600',
                            background: `${statusColor}1a`, color: statusColor }}>
                            {displayStatus}
                          </span>
                        </td>
                        <td style={{ padding: '14px 24px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                          {inv.invitedByEmail || '—'}
                        </td>
                        <td style={{ padding: '14px 24px', color: 'var(--text-secondary)', fontSize: '13px' }}>
                          {inv.expiresAt ? new Date(inv.expiresAt).toLocaleDateString() : '—'}
                        </td>
                        <td style={{ padding: '14px 24px', textAlign: 'right' }}>
                          {isPending && !isExpired && ['OWNER', 'MANAGER'].includes(team?.membership?.role) && (
                            <Button variant="ghost" style={{ fontSize: '13px', padding: '5px 10px' }}
                              onClick={async () => {
                                try {
                                  await cancelTeamInvitation(inv.id);
                                  await fetchTeamData();
                                } catch (e) { console.error(e); }
                              }}
                            >Cancel</Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* CATEGORIES TAB */}
        {activeTab === 'categories' && (
          <div className="glass-panel" style={{ borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>Team Categories</h3>
              <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{categories.length} total</span>
            </div>
            {categories.length === 0 ? (
              <div style={{ padding: '48px 0', textAlign: 'center', color: 'var(--text-secondary)' }}>
                <Folder size={36} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
                <p style={{ margin: 0 }}>No categories created for this team yet.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '16px', padding: '20px 24px' }}>
                {categories.map((cat) => (
                  <div key={cat.id} style={{
                    padding: '16px', background: 'var(--bg-secondary)',
                    borderRadius: '10px', border: '1px solid var(--border-color)',
                    display: 'flex', flexDirection: 'column', gap: '6px'
                  }}>
                    <div style={{ fontWeight: '600', fontSize: '15px' }}>{cat.name}</div>
                    {cat.description && <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{cat.description}</div>}
                    <div style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '4px' }}>
                      Created {new Date(cat.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SETTINGS TAB */}
        {activeTab === 'settings' && (
          <div style={{ display: 'grid', gap: '24px', maxWidth: '600px' }}>
            <div className="glass-panel" style={{ padding: '24px', borderRadius: '12px' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>Team Settings</h3>
              <form onSubmit={handleUpdateTeam} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Input label="Team Name" value={teamName} onChange={(e) => setTeamName(e.target.value)} required />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500' }}>Description</label>
                  <textarea 
                    value={teamDescription} onChange={(e) => setTeamDescription(e.target.value)}
                    style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', minHeight: '100px', resize: 'vertical' }}
                  />
                </div>
                <Button type="submit">Save Changes</Button>
              </form>
            </div>

            <div className="glass-panel" style={{ padding: '24px', borderRadius: '12px', border: '1px solid var(--danger)' }}>
              <h3 style={{ margin: '0 0 16px', fontSize: '18px', color: 'var(--danger)' }}>Danger Zone</h3>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>Leaving the team will immediately revoke your access to all shared resources.</p>
              <Button variant="danger" onClick={handleLeave}>Leave Team</Button>
            </div>
          </div>
        )}

        {/* PERMISSIONS TAB */}
        {activeTab === 'permissions' && (
          <div className="glass-panel" style={{ padding: '24px', borderRadius: '12px', maxWidth: '800px' }}>
            <h3 style={{ margin: '0 0 16px', fontSize: '18px' }}>Role Permissions</h3>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>Edit raw JSON permissions matrix for Owner, Manager, and Member roles.</p>
            <textarea
              value={permissionsJson}
              onChange={(e) => setPermissionsJson(e.target.value)}
              style={{ width: '100%', minHeight: '400px', padding: '16px', fontFamily: 'monospace', fontSize: '13px', background: '#1e1e1e', color: '#d4d4d4', borderRadius: '8px', border: '1px solid var(--border-color)', marginBottom: '16px' }}
              spellCheck={false}
            />
            <Button onClick={handleSavePermissions}>Save Permissions</Button>
          </div>
        )}
      </div>

      {/* Invite Member Drawer */}
      {isInviteOpen && (
        <div style={{
          position: 'fixed', top: 0, right: 0, width: '400px', height: '100vh',
          background: 'var(--bg-primary, #ffffff)', boxShadow: '-4px 0 24px rgba(0,0,0,0.1)',
          zIndex: 100, display: 'flex', flexDirection: 'column', borderLeft: '1px solid var(--border-color)'
        }}>
          <div style={{ padding: '24px', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ margin: 0, fontSize: '20px' }}>Invite Member</h2>
            <button onClick={() => setIsInviteOpen(false)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}>
              <X size={20} />
            </button>
          </div>
          <div style={{ padding: '24px', flex: 1, overflowY: 'auto' }}>
            <form onSubmit={handleInvite} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <Input label="Email Address" type="email" value={memberEmail} onChange={(e) => setMemberEmail(e.target.value)} placeholder="colleague@company.com" required />
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: '500' }}>Role</label>
                <select value={memberRole} onChange={(e) => setMemberRole(e.target.value)} style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                  <option value="MEMBER">Member (Access authorized shared resources)</option>
                  <option value="MANAGER">Manager (Manage members & share items)</option>
                  <option value="OWNER">Owner (Full control)</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '14px', fontWeight: '500' }}>Invitation Expiration</label>
                <select value={expiresIn} onChange={(e) => setExpiresIn(e.target.value)} style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-secondary)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
                  <option value="1 hour">1 hour</option>
                  <option value="1 day">1 day</option>
                  <option value="5 days">5 days</option>
                  <option value="30 days">30 days</option>
                </select>
              </div>

              <div style={{ marginTop: 'auto', paddingTop: '24px' }}>
                <Button type="submit" style={{ width: '100%' }}>Send Invitation</Button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {isInviteOpen && (
        <div onClick={() => setIsInviteOpen(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 99 }} />
      )}
    </MainLayout>
  );
}
